import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import { Box } from "@/components/ui/box";
import { Text } from "@/components/ui/text";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { ScrollView } from "@/components/ui/scroll-view";
import { VStack } from "@/components/ui/vstack";
import {
  getUserControllerMeQueryKey,
  useUserBeneficiaryControllerUploadDocument,
  useUserControllerMe,
} from "@/generated/api/api";
import { getName } from "@/utils/name";
import {
  ComponentPropsWithoutRef,
  PropsWithChildren,
  ReactElement,
  forwardRef,
  useState,
} from "react";
import { Divider } from "@/components/ui/divider";
import { UserDTO } from "@/generated/api/models";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import {
  BookOpen,
  BookText,
  ChevronRight,
  Edit,
  FileText,
  Globe,
  Info,
  LogOut,
  LucideIcon,
  MessageCircleQuestion,
  Settings,
  Trash2,
} from "lucide-react-native";
import { Platform, RefreshControl } from "react-native";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";
import { usePayoutFrequency } from "./usePayoutFrequency";
import { Card } from "@/components/ui/card";
import { Icon } from "@/components/ui/icon";
import { Link as ExpoLink } from "expo-router";
import { Pressable } from "@/components/ui/pressable";
import ChatWootWidget from "@chatwoot/react-native-widget";
import { getI18n } from "react-i18next";
import { useColorScheme } from "@/components/useColorScheme";
import { useSession } from "@/utils/session.ctx";
import {
  ProfileActionSheet,
  ProfileManageAccountSheet,
  ProfilePayoutActionSheet,
  ProfileQRCodeModalEdit,
} from "./modals";
import { ProfileBeneficiaryModalEdit } from "./beneficiary.modal";
import { useQueryClient } from "@tanstack/react-query";
import { BeneficiaryActionSheet } from "./beneficiary.action.sheet";
import { uriToBlob } from "@/utils/uri.to.blob";
import * as WebBrowser from "expo-web-browser";
import { Spinner } from "@/components/ui/spinner";

export type ProfileSectionProps = PropsWithChildren & {
  title?: string;
  subtitle?: string;
  action?: ReactElement;
};

function ProfileSection(props: ProfileSectionProps) {
  return (
    <VStack className="gap-2">
      <HStack className="justify-between items-center w-full gap-2">
        {props.title && (
          <VStack className="flex-1">
            <Heading size="xl">{props.title}</Heading>
            {props.subtitle && <Text size="sm">{props.subtitle}</Text>}
          </VStack>
        )}
        {props.action}
      </HStack>
      <Box className="p-4 border rounded-md border-background-300">
        {props.children}
      </Box>
    </VStack>
  );
}

const UpdateButton = forwardRef<any, ComponentPropsWithoutRef<typeof Button>>(
  (props, ref) => (
    <Button ref={ref} variant="link" size="lg" {...props}>
      <ButtonIcon as={Edit} />
    </Button>
  )
);

export function ProfilePage() {
  const { data: user, refetch, isRefetching } = useUserControllerMe();
  const payoutFrequency = usePayoutFrequency(user?.payoutFrequency);

  const [openDeleteAccount, setOpenDeleteAccount] = useState(false);
  const [openSupportChat, setOpenSupportChat] = useState(false);
  const [openQr, setOpenQr] = useState(false);
  const [openPayout, setOpenPayout] = useState(false);
  const [openBeneficiary, setOpenBeneficiary] = useState(false);
  const [openBeneficiaryDocument, setOpenBeneficiaryDocument] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const {
    mutateAsync: uploadBeneficiaryDocument,
    isPending: isBeneficiaryDocumentUploadingPending,
  } = useUserBeneficiaryControllerUploadDocument();
  const queryClient = useQueryClient();
  const locale = getI18n().language;
  const colorScheme = useColorScheme();
  const session = useSession();

  return (
    <>
      <Box className="w-full h-full">
        <ScrollView
          className="flex-1"
          contentContainerClassName="px-4 pt-4 pb-4 grow"
          refreshControl={
            <RefreshControl onRefresh={refetch} refreshing={isRefetching} />
          }
        >
          <VStack className="gap-4 w-full">
            <ProfileCard user={user} />
            <Divider className="my-2" />
            <ProfileSection
              title="QR-code info"
              subtitle="What tippers see on your page"
              action={<UpdateButton onPress={() => setOpenQr(true)} />}
            >
              <VStack className="gap-2">
                <VStack>
                  <Heading size="md">Display name</Heading>
                  <Text>{user?.displayName || getName(user)}</Text>
                </VStack>
                <VStack>
                  <Heading size="md">Collection goal</Heading>
                  <Text>
                    {user?.collectingGoal ?? (
                      <Text italic>No collection goal</Text>
                    )}
                  </Text>
                </VStack>
              </VStack>
            </ProfileSection>
            <ProfileSection
              title="Payout info"
              action={
                user && (
                  <ProfilePayoutActionSheet
                    isOpen={openPayout}
                    onOpen={() => setOpenPayout(true)}
                    onClose={() => setOpenPayout(false)}
                    user={user}
                    trigger={(triggerProps) => (
                      <UpdateButton {...triggerProps} />
                    )}
                  />
                )
              }
            >
              <VStack className="gap-2">
                {!user?.beneficiary ? (
                  <Alert action="info">
                    <AlertIcon as={Info} className="mt-1" />
                    <HStack className="justify-between flex-1 items-center gap-3 sm:gap-8">
                      <AlertText className="flex-1">
                        Please, update your banking details to unlock all
                        payment methods and proceed with payouts.
                      </AlertText>
                      <Button
                        size="sm"
                        onPress={() => setOpenBeneficiary(true)}
                      >
                        <ButtonText>Update</ButtonText>
                      </Button>
                    </HStack>
                  </Alert>
                ) : user.beneficiary.status === "PENDING" ? (
                  <Alert action="warning">
                    <AlertIcon as={Info} className="mt-1" />
                    <HStack className="justify-between flex-1 items-center gap-3 sm:gap-8">
                      <AlertText className="flex-1">
                        Please, upload{" "}
                        <AlertText bold>IBAN certificate</AlertText> or{" "}
                        <AlertText bold>Account confirmation</AlertText> or any
                        other document where name of IBAN account might be
                        verified.
                      </AlertText>
                      <BeneficiaryActionSheet
                        isOpen={openBeneficiaryDocument}
                        onClose={() => setOpenBeneficiaryDocument(false)}
                        onOpen={() => setOpenBeneficiaryDocument(true)}
                        trigger={(props) => (
                          <Button
                            size="sm"
                            isDisabled={isBeneficiaryDocumentUploadingPending}
                            {...props}
                          >
                            <ButtonText>Upload</ButtonText>
                            {isBeneficiaryDocumentUploadingPending && (
                              <ButtonSpinner />
                            )}
                          </Button>
                        )}
                        onUpload={(imgUri) => {
                          const upload = (uriOrBlob: any) =>
                            uploadBeneficiaryDocument(
                              {
                                data: {
                                  file: uriOrBlob,
                                },
                              },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getUserControllerMeQueryKey(),
                                  });
                                },
                              }
                            ).catch((e) => console.error(e));

                          uriToBlob(imgUri).then(upload);
                        }}
                      />
                    </HStack>
                  </Alert>
                ) : user.beneficiary.status === "VERIFICATION" ? (
                  <Alert action="muted">
                    <Spinner />
                    <AlertText className="flex-1">
                      Your banking details is being verified, usually it takes
                      up to 3 hours.
                    </AlertText>
                  </Alert>
                ) : user.beneficiary.status === "DECLINED" ? (
                  <Alert action="error">
                    <AlertIcon as={Info} className="mt-1" />
                    <HStack className="justify-between flex-1 items-center gap-3 sm:gap-8">
                      <AlertText className="flex-1">
                        Your document has been rejected due to:{" "}
                        {user.beneficiary.reason ?? "internal"}. If you
                        experiencing a trouble or have a questions - feel free
                        to contact support.
                      </AlertText>
                      <BeneficiaryActionSheet
                        isOpen={openBeneficiaryDocument}
                        onClose={() => setOpenBeneficiaryDocument(false)}
                        onOpen={() => setOpenBeneficiaryDocument(true)}
                        trigger={(props) => (
                          <Button
                            size="sm"
                            isDisabled={isBeneficiaryDocumentUploadingPending}
                            {...props}
                          >
                            <ButtonText>Upload</ButtonText>
                            {isBeneficiaryDocumentUploadingPending && (
                              <ButtonSpinner />
                            )}
                          </Button>
                        )}
                        onUpload={(imgUri) => {
                          const upload = (uriOrBlob: any) =>
                            uploadBeneficiaryDocument(
                              {
                                data: {
                                  file: uriOrBlob,
                                },
                              },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getUserControllerMeQueryKey(),
                                  });
                                },
                              }
                            ).catch((e) => console.error(e));

                          uriToBlob(imgUri).then(upload);
                        }}
                      />
                    </HStack>
                  </Alert>
                ) : null}
                <VStack>
                  <Heading size="md">IBAN</Heading>
                  <Text>
                    {user?.beneficiary?.iban ?? (
                      <Text italic>No IBAN specified</Text>
                    )}
                  </Text>
                </VStack>
                <VStack>
                  <Heading size="md">Payout frequency</Heading>
                  <Text>{payoutFrequency}</Text>
                </VStack>
              </VStack>
            </ProfileSection>

            <ProfileSection title="Support">
              <ExpoLink
                href="https://help.gotips.co"
                target="_blank"
                onPress={(e) => {
                  if (Platform.OS === "android" || Platform.OS === "ios") {
                    e.preventDefault();
                    WebBrowser.openBrowserAsync("https://help.gotips.co");
                  }
                }}
              >
                <ProfileSettingsItem
                  icon={BookOpen}
                  title="Help center"
                  subtitle="Guides and docs"
                />
              </ExpoLink>
              <Divider className="my-1" />
              <Pressable
                onPress={() => {
                  setOpenSupportChat(true);
                }}
              >
                <ProfileSettingsItem
                  icon={MessageCircleQuestion}
                  title="Chat with support"
                />
              </Pressable>
              <Divider className="my-1" />
              <Pressable
                onPress={() => {
                  setOpenDeleteAccount(true);
                }}
              >
                <ProfileSettingsItem
                  icon={Settings}
                  title="Manage my account"
                />
              </Pressable>
            </ProfileSection>

            <ProfileSection title="Service">
              <ExpoLink href="https://gotips.co/client-terms" target="_blank">
                <ProfileSettingsItem
                  icon={FileText}
                  title="Terms and conditions"
                />
              </ExpoLink>
              <Divider className="my-1" />
              <ExpoLink href="https://gotips.co/privacy-policy" target="_blank">
                <ProfileSettingsItem icon={FileText} title="Privacy policy" />
              </ExpoLink>
              <Divider className="my-1" />
              <ExpoLink href="https://gotips.co/" target="_blank">
                <ProfileSettingsItem icon={Globe} title="gotips.co" />
              </ExpoLink>
            </ProfileSection>

            <Button
              action="negative"
              variant="outline"
              isDisabled={logoutPending}
              onPress={() => {
                setLogoutPending(true);
                session.signOut().finally(() => {
                  setLogoutPending(false);
                });
              }}
            >
              <ButtonIcon as={LogOut} />
              <ButtonText className="text-red-600">Logout</ButtonText>
              {logoutPending && <ButtonSpinner />}
            </Button>
          </VStack>
        </ScrollView>
      </Box>
      {user && (
        <ProfileQRCodeModalEdit
          isOpen={openQr}
          onClose={() => setOpenQr(false)}
          user={user}
        />
      )}
      {user && (
        <ProfileBeneficiaryModalEdit
          isOpen={openBeneficiary}
          onClose={() => setOpenBeneficiary(false)}
          user={user}
        />
      )}
      {openSupportChat && (
        <ChatWootWidget
          websiteToken="4xA2sLGTzrRfs99WidcMzE6v"
          locale={locale}
          baseUrl="https://app.chatwoot.com"
          closeModal={() => setOpenSupportChat(false)}
          isModalVisible={openSupportChat}
          colorScheme={colorScheme ?? "auto"}
          user={{
            identifier: user?.id,
            avatar_url: user?.avatarImage ?? undefined,
            email: user?.email,
            name: getName(user),
          }}
        />
      )}
      <ProfileManageAccountSheet
        isOpen={openDeleteAccount}
        onClose={() => setOpenDeleteAccount(false)}
      />
    </>
  );
}

function ProfileCard({ user }: { user: UserDTO | undefined }) {
  const [openActionSheet, setOpenActionSheet] = useState(false);

  return (
    <>
      <HStack space="md" className="justify-between items-center w-full">
        <HStack space="md" className="items-center flex-1">
          <Avatar className="bg-primary-500" size="lg">
            <AvatarFallbackText>{getName(user)}</AvatarFallbackText>
            <AvatarImage
              source={{
                uri: user?.avatarImage ?? undefined,
              }}
            />
          </Avatar>
          <VStack className="flex-1">
            <Heading>{getName(user)}</Heading>
            <Text size="sm">{user?.email}</Text>
          </VStack>
        </HStack>
        {user && (
          <ProfileActionSheet
            isOpen={openActionSheet}
            onClose={() => setOpenActionSheet(false)}
            onOpen={() => setOpenActionSheet(true)}
            user={user}
            trigger={(triggerProps) => <UpdateButton {...triggerProps} />}
          />
        )}
      </HStack>
    </>
  );
}

function ProfileSettingsItem({
  icon,
  title,
  subtitle,
}: {
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
}) {
  return (
    <Card
      size="sm"
      variant="ghost"
      className="flex-row justify-between w-full items-center px-0"
    >
      <HStack className="items-center" space="md">
        {icon && <Icon as={icon} size="lg" className="mr-2" />}
        <VStack>
          <Heading
            size="xs"
            className="text-ellipsis overflow-hidden whitespace-nowrap font-medium"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {title}
          </Heading>
          {subtitle && <Text size="xs">{subtitle}</Text>}
        </VStack>
      </HStack>
      <Icon as={ChevronRight} />
    </Card>
  );
}
